import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { format } from "date-fns";
import { useState } from "react";
import { FaStar } from "react-icons/fa";
import { LuCalendarClock } from "react-icons/lu";
import {
  MdAccessTime,
  MdCheckCircle,
  MdCancel,
  MdExpandLess,
  MdExpandMore,
  MdLocalOffer,
  MdLocationOn,
  MdOutlineWork,
} from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import ReactStars from "react-rating-stars-component";
import { baseURL } from "../../../config/apiHandle/apiHandle";
import { PostReviewAsync, GetAllJobsAsync } from "../../../services/services";
import { themeOrange } from "../../../utils/colorTheme";
import { imgUrl } from "../../../utils/imageUrl";
import { getServiceLocalImage } from "../../../utils/serviceImages";
import {
  error_toast_message,
  success_toast_message,
} from "../../../utils/toast_message";
import CustomButton from "../../common/Button/Button";
import { asyncStatus } from "../../../utils/asyncStatus";
import axios from "axios";

const CompletedJobsTabWithoutPayment = () => {
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [jobReview, setjobReview] = useState({});
  const [reloadKey, setReloadKey] = useState(0);

  const dispatch = useDispatch();
  const theme = useTheme();

  const { get_all_jobs_data, post_review_status } = useSelector(
    (state) => state.serviceManage
  );

  const completedJobs = get_all_jobs_data?.filter(
    (job) =>
      job.status === "Completed" ||
      job.status === "Cancelled" ||
      job.status === "Paid"
  ) || [];

  const getInvoiceData = (job) => {
    const servicePrice = job?.service?.price || 0;
    const serviceAmount = job?.service_amount || 0;
    const tax = job?.tax || 0;
    const totalAmount = job?.total_amount || 0;
    const memberDiscount = job?.type === "Member" ? 25 : 0;

    let couponDiscount = 0;
    if (job?.coupon && job?.coupon_percentage > 0) {
      if (job?.coupon_type === 1) {
        const beforeCouponAmount = serviceAmount / (1 - job.coupon_percentage / 100);
        couponDiscount = beforeCouponAmount - serviceAmount;
      } else if (job?.coupon_type === 0) {
        couponDiscount = job?.coupon_percentage;
      }
    }

    return {
      originalServicePrice: servicePrice,
      memberDiscount,
      couponDiscount,
      serviceAmount,
      tax,
      totalAmount,
      couponName: job?.coupon,
      couponPercentage: job?.coupon_percentage,
      couponType: job?.coupon_type,
      addOnsTotal: job?.add_ons?.reduce((sum, addon) => sum + (addon.price || 0), 0) || 0,
    };
  };

  const parseDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return null;
    const parts = dateStr.split("-");
    if (parts.length !== 3) return null;
    const [year, month, day] = parts;
    const d = new Date(year, month - 1, day);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDate = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return null;
    return format(d, "EEE, MMM d, yyyy");
  };

  const handleViewDetails = (jobId) => {
    setSelectedJobId((prev) => (prev === jobId ? null : jobId));
  };

  if (!completedJobs.length) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "60vh" }} spacing={2}>
        <Box sx={{ width: 80, height: 80, borderRadius: "50%", backgroundColor: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <MdOutlineWork size={36} color="#ccc" />
        </Box>
        <Typography sx={{ color: "#aaa", fontWeight: "500", fontSize: "16px" }}>
          No completed jobs found
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} key={reloadKey}>
      {completedJobs
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .map((job) => {
          const isSelected = selectedJobId === job?.id;
          const isCancelled = job?.status === "Cancelled";
          const isCompleted = job?.status === "Completed";
          const isPaid = job?.status === "Paid";
          const serviceName = job?.service?.name || "Service";
          const dateStr = job?.date_time_lock?.date;
          const invoiceData = getInvoiceData(job);
          const vendorName = `${job?.pro?.first_name || ""} ${job?.pro?.last_name || ""}`.trim();
          const vendorInitials = vendorName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

          // Stripe color/label per status
          const stripeGradient = isCancelled
            ? "linear-gradient(90deg, #c62828 0%, #ef5350 100%)"
            : "linear-gradient(90deg, #2e7d32 0%, #4CAF50 100%)";
          const stripeLabel = isCancelled ? "CANCELLED" : isPaid ? "PAID" : "COMPLETED";
          const chipBg = isCancelled ? "#ffeaea" : "#e8f5e9";
          const chipColor = isCancelled ? "#c62828" : "#2e7d32";
          const chipBorder = isCancelled ? "#ef9a9a" : "#a5d6a7";

          return (
            <Box
              key={job?.id}
              sx={{
                borderRadius: "14px",
                border: "1.5px solid #ebebeb",
                backgroundColor: "white",
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                transition: "box-shadow 0.2s",
              }}
            >
              {/* Status stripe */}
              <Box sx={{ px: 3, py: 0.75, background: stripeGradient, display: "flex", alignItems: "center", gap: 1 }}>
                {!isCancelled && <MdCheckCircle size={14} color="white" />}
                {isCancelled && <MdCancel size={14} color="white" />}
                <Typography sx={{ color: "white", fontSize: "12px", fontWeight: "700", letterSpacing: "0.5px" }}>
                  {stripeLabel}
                </Typography>
              </Box>

              {/* Main card row */}
              <Stack direction={{ xs: "column", sm: "row" }} sx={{ p: 2.5 }} gap={2}>
                {/* Service image */}
                <Box
                  sx={{
                    width: { xs: "100%", sm: 110 },
                    height: { xs: 140, sm: 110 },
                    borderRadius: "10px",
                    overflow: "hidden",
                    flexShrink: 0,
                    filter: isCancelled ? "grayscale(60%)" : "grayscale(15%)",
                  }}
                >
                  <img
                    src={getServiceLocalImage(serviceName)}
                    alt={serviceName}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Box>

                {/* Content */}
                <Stack flex={1} spacing={1.5}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Typography sx={{ fontWeight: "700", fontSize: "17px", color: "#1a1a1a", lineHeight: 1.3 }}>
                      {serviceName}
                    </Typography>
                    {invoiceData.totalAmount > 0 && (
                      <Typography sx={{ fontWeight: "800", fontSize: "18px", color: isCancelled ? "#999" : "#2e7d32", flexShrink: 0, textDecoration: isCancelled ? "line-through" : "none" }}>
                        ${(+invoiceData.totalAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    )}
                  </Stack>

                  <Stack spacing={0.75}>
                    {dateStr && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <LuCalendarClock size={16} color="#999" style={{ flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "13px", color: "#666" }}>
                          {formatDate(dateStr)}
                        </Typography>
                        {job?.date_time_lock?.shift && (
                          <Typography sx={{ fontSize: "12px", color: "#999" }}>
                            ({job.date_time_lock.shift.slice(0, 3)})
                          </Typography>
                        )}
                      </Stack>
                    )}
                    {job?.date_time_lock?.time && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <MdAccessTime size={16} color="#999" style={{ flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "13px", color: "#888" }}>
                          {job.date_time_lock.time}
                        </Typography>
                      </Stack>
                    )}
                    {job?.location && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <MdLocationOn size={16} color="#999" style={{ flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "13px", color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "320px" }}>
                          {job.location}
                        </Typography>
                      </Stack>
                    )}
                    {job?.coupon && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <MdLocalOffer size={16} color="#4caf50" style={{ flexShrink: 0 }} />
                        <Chip
                          label={
                            job.coupon_type === 1
                              ? `${job.coupon} (${job.coupon_percentage}% OFF)`
                              : `${job.coupon} ($${job.coupon_percentage} OFF)`
                          }
                          size="small"
                          sx={{ backgroundColor: "#e8f5e8", color: "#4caf50", fontWeight: "600", fontSize: "11px" }}
                        />
                      </Stack>
                    )}
                    {job?.payments_status === "Paid" && (
                      <Chip
                        label="Paid"
                        size="small"
                        sx={{ width: "fit-content", backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: "700", fontSize: "11px", border: "1px solid #a5d6a7" }}
                      />
                    )}
                  </Stack>
                </Stack>

                {/* Right: status chip + expand */}
                <Stack alignItems={{ xs: "flex-start", sm: "flex-end" }} justifyContent="space-between" flexShrink={0} gap={1}>
                  <Chip
                    label={job.status}
                    size="small"
                    sx={{ backgroundColor: chipBg, color: chipColor, fontWeight: "700", fontSize: "11px", border: `1px solid ${chipBorder}` }}
                  />
                  <Box
                    sx={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: `${themeOrange}10`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                    onClick={() => handleViewDetails(job?.id)}
                  >
                    {isSelected ? <MdExpandLess size={20} color={themeOrange} /> : <MdExpandMore size={20} color={themeOrange} />}
                  </Box>
                </Stack>
              </Stack>

              {/* Expanded section */}
              <Collapse in={isSelected}>
                <Box sx={{ px: 3, pb: 3, borderTop: "1px solid #f0f0f0", pt: 2.5 }}>
                  <Stack spacing={2.5}>

                    {/* Job Description */}
                    {job?.description && (
                      <Stack spacing={1}>
                        <Typography sx={{ fontWeight: "700", fontSize: "15px", color: "#1a1a1a" }}>Job Description</Typography>
                        <Box sx={{ p: 2, backgroundColor: "#fafafa", borderRadius: "10px", border: "1px solid #f0f0f0", borderLeft: `3px solid ${themeOrange}` }}>
                          <Typography sx={{ color: "#555", fontSize: "14px", lineHeight: 1.6 }}>{job.description}</Typography>
                        </Box>
                      </Stack>
                    )}

                    {/* Invoice */}
                    <Paper
                      elevation={0}
                      sx={{ p: { xs: 2, sm: 3 }, backgroundColor: "#fafafa", borderRadius: "12px", border: "1px solid #f0f0f0" }}
                    >
                      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold", color: theme.palette.primary.main }}>
                          Invoice
                        </Typography>
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            Date: {new Date(job?.created_at).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Invoice #: INV-{job?.id}
                          </Typography>
                          {job?.type === "Member" && (
                            <Typography variant="body2" color="textSecondary">Member Discount Applied</Typography>
                          )}
                          {job?.coupon && (
                            <Typography variant="body2" sx={{ color: "#4caf50", fontWeight: "600" }}>
                              Coupon: {job.coupon}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Divider sx={{ mb: 2 }} />

                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                              <TableCell sx={{ fontWeight: "bold" }}>Description</TableCell>
                              <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>
                                <Typography fontWeight="bold">{job?.service?.name}</Typography>
                                <Typography variant="body2">{job?.service?.description}</Typography>
                              </TableCell>
                              <TableCell sx={{ textAlign: "right" }}>
                                ${Number(invoiceData.originalServicePrice || 0).toFixed(2)}
                              </TableCell>
                            </TableRow>

                            {invoiceData.memberDiscount > 0 && (
                              <TableRow>
                                <TableCell>
                                  <Typography fontStyle="italic" sx={{ color: theme.palette.info.main }}>
                                    Member Discount
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ textAlign: "right", color: theme.palette.info.main }}>
                                  -${Number(invoiceData.memberDiscount || 0).toFixed(2)}
                                </TableCell>
                              </TableRow>
                            )}

                            {job?.add_ons?.map((addon, index) => (
                              <TableRow key={index}>
                                <TableCell><Typography>Add-on: {addon.name}</Typography></TableCell>
                                <TableCell sx={{ textAlign: "right" }}>${Number(addon.price || 0).toFixed(2)}</TableCell>
                              </TableRow>
                            ))}

                            {job?.coupon && (
                              <TableRow>
                                <TableCell>
                                  <Typography fontStyle="italic" sx={{ color: "#4caf50" }}>
                                    Coupon ({job.coupon} - {job.coupon_type === 1 ? `${job.coupon_percentage}%` : `$${job.coupon_percentage}`})
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ textAlign: "right", color: "#4caf50", fontWeight: "600" }}>
                                  -${Number(invoiceData.couponDiscount || 0).toFixed(2)}
                                </TableCell>
                              </TableRow>
                            )}

                            <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                              <TableCell><Typography fontWeight="600">Service Amount (after discounts)</Typography></TableCell>
                              <TableCell sx={{ textAlign: "right", fontWeight: "600" }}>
                                ${Number(job.service_amount || 0).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Grid container spacing={1} sx={{ maxWidth: 360, ml: "auto", mt: 2 }}>
                        <Grid item xs={8}>
                          <Typography variant="body1">Subtotal:</Typography>
                          <Typography variant="body1">Tax (13%):</Typography>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>Total:</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Typography variant="body1">${Number(job.service_amount || 0).toFixed(2)}</Typography>
                          <Typography variant="body1">${Number(job.tax || 0).toFixed(2)}</Typography>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="h6" sx={{ fontWeight: "bold", color: isCancelled ? "#999" : theme.palette.success.main }}>
                            ${Number(job.total_amount || 0).toFixed(2)}
                          </Typography>
                        </Grid>
                      </Grid>

                      {(invoiceData.memberDiscount > 0 || invoiceData.couponDiscount > 0) && (
                        <Box sx={{ mt: 2, p: 1.5, backgroundColor: "#f0fdf4", borderRadius: "8px", textAlign: "center" }}>
                          <Typography variant="body2" color="success.main" fontWeight="600">
                            You saved ${(Number(invoiceData.memberDiscount || 0) + Number(invoiceData.couponDiscount || 0)).toFixed(2)} on this booking!
                          </Typography>
                        </Box>
                      )}

                      <Divider sx={{ mt: 2, mb: 1.5 }} />
                      <Typography variant="body2" sx={{ fontStyle: "italic", textAlign: "center", color: "#999" }}>
                        Thank you for your business. Payments payable to Jiff Done.
                      </Typography>
                    </Paper>

                    {/* Rate Your Pro */}
                    {isCompleted && job?.vendor && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 2, sm: 3 },
                          borderRadius: "12px",
                          border: job?.review ? "2px solid #4caf50" : `2px solid ${themeOrange}`,
                          backgroundColor: "#fff",
                        }}
                      >
                        <Stack gap={2}>
                          <Typography
                            sx={{
                              fontWeight: "700",
                              fontSize: "16px",
                              color: job?.review ? "#2e7d32" : themeOrange,
                              textAlign: "center",
                            }}
                          >
                            {job?.review ? "Your Review" : "Rate Your Pro"}
                          </Typography>

                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            gap={2}
                            sx={{ backgroundColor: "#f9f9f9", padding: "16px", borderRadius: "10px" }}
                          >
                            <Avatar
                              src={job?.vendor?.avatar ? `${imgUrl}${job.vendor.avatar}` : undefined}
                              sx={{ width: 52, height: 52, border: `2px solid ${themeOrange}`, backgroundColor: "#071d5b", fontWeight: "700" }}
                            >
                              {vendorInitials || job?.vendor?.first_name?.[0]}
                            </Avatar>
                            <Stack>
                              <Typography fontWeight="600" fontSize="15px">{vendorName}</Typography>
                              <Typography variant="body2" color="text.secondary">{serviceName}</Typography>
                            </Stack>
                          </Stack>

                          {job?.review ? (
                            <Stack gap={1} alignItems="center">
                              <Stack direction="row" alignItems="center" gap={1}>
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <FaStar key={star} size={26} color={star <= (job?.review?.rating || 0) ? "#ffd700" : "#e0e0e0"} />
                                ))}
                              </Stack>
                              {job?.review?.msg && (
                                <Box sx={{ backgroundColor: "#f5f5f5", p: "12px 16px", borderRadius: "8px", width: "100%", mt: 1 }}>
                                  <Typography sx={{ fontStyle: "italic", color: "#555", fontSize: "14px" }}>
                                    "{job.review.msg}"
                                  </Typography>
                                </Box>
                              )}
                              <Typography variant="caption" color="success.main">Thank you for your feedback!</Typography>
                            </Stack>
                          ) : (
                            <Stack gap={2} alignItems="center">
                              <Typography variant="body2" color="text.secondary" textAlign="center">
                                How was your experience with {job?.vendor?.first_name}?
                              </Typography>
                              <Box sx={{ display: "flex", justifyContent: "center" }}>
                                <ReactStars
                                  count={5}
                                  value={jobReview[job.id]?.rating || 0}
                                  onChange={(newRating) =>
                                    setjobReview({ ...jobReview, [job.id]: { ...jobReview[job.id], rating: newRating } })
                                  }
                                  size={38}
                                  activeColor="#ffd700"
                                  isHalf={false}
                                  emptyIcon={<FaStar />}
                                  fullIcon={<FaStar />}
                                />
                              </Box>
                              <TextField
                                multiline
                                rows={3}
                                placeholder="Tell us about your experience (optional)"
                                value={jobReview[job.id]?.msg || ""}
                                onChange={(e) =>
                                  setjobReview({ ...jobReview, [job.id]: { ...jobReview[job.id], msg: e.target.value } })
                                }
                                fullWidth
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                    "&:hover fieldset": { borderColor: themeOrange },
                                    "&.Mui-focused fieldset": { borderColor: themeOrange },
                                  },
                                }}
                              />
                              <CustomButton
                                onClick={() => {
                                  const review = jobReview[job.id];
                                  if (!review?.rating) {
                                    error_toast_message("Please select a rating");
                                    return;
                                  }
                                  dispatch(PostReviewAsync({ msg: review.msg || "", rating: review.rating, oid: job.id }))
                                    .then((result) => {
                                      if (result.meta.requestStatus === "fulfilled") {
                                        success_toast_message("Review submitted successfully!");
                                        setjobReview({ ...jobReview, [job.id]: { msg: "", rating: 0 } });
                                        dispatch(GetAllJobsAsync());
                                      }
                                    });
                                }}
                                disabled={!jobReview[job.id]?.rating || post_review_status === asyncStatus.LOADING}
                                style={{
                                  background: "var(--Orange, linear-gradient(180deg, #F15A24 0%, #C53F10 100%))",
                                  padding: "12px 40px",
                                  borderRadius: "25px",
                                  opacity: !jobReview[job.id]?.rating ? 0.6 : 1,
                                }}
                              >
                                {post_review_status === asyncStatus.LOADING ? (
                                  <CircularProgress size={20} sx={{ color: "white" }} />
                                ) : (
                                  <Typography sx={{ fontSize: "14px !important", fontWeight: "600" }}>
                                    Submit Review
                                  </Typography>
                                )}
                              </CustomButton>
                            </Stack>
                          )}
                        </Stack>
                      </Paper>
                    )}

                  </Stack>
                </Box>
              </Collapse>
            </Box>
          );
        })}
    </Stack>
  );
};

const CompletedJobsTab = () => <CompletedJobsTabWithoutPayment />;

export default CompletedJobsTab;
